// Drill-down modal. v1: shows aggregate stats + an hourly sparkline over the
// selected window. Sample tweets are deferred to v2 (different X API).

import { useEffect, useState } from "react";
import { COLORS, fmtRaw, tStyles } from "../styles";
import { api, fmtMillsUsd, useHasAdminKey } from "../api";
import { aggregate, nowAlignedToHourSec } from "../aggregate";
import type { CountBucket, QuerySpec } from "../../shared/types";

const HOUR = 3600;

interface Props {
  topic: { name: string; spec: QuerySpec; buckets: CountBucket[] };
  tf: "24h" | "7d" | "30d";
  // anchor for window math; null when no fresh data, fall back to wall clock
  dataAsOf: number | null;
  watched: boolean;
  onClose: () => void;
  onToggleWatch: () => void;
}

export function DrillDown({ topic, tf, dataAsOf, watched, onClose, onToggleWatch }: Props) {
  const agg = aggregate(topic.buckets, dataAsOf ?? undefined);
  const [hires, setHires] = useState<CountBucket[] | null>(null);

  useEffect(() => {
    // Fetch a finer slice for the drill-down chart (always 7d hourly).
    const end = nowAlignedToHourSec();
    const start = end - 7 * 24 * HOUR;
    api
      .series(topic.spec.query_hash, start, end + HOUR, "hour")
      .then((r) => setHires(r.buckets))
      .catch(() => setHires([]));
  }, [topic.spec.query_hash]);

  const seriesPoints = [...(hires ?? topic.buckets)].sort(
    (a, b) => a.bucket_start - b.bucket_start,
  );
  const peakPerHr =
    seriesPoints.length > 0
      ? Math.max(...seriesPoints.map((p) => p.tweet_count))
      : 0;
  const max = Math.max(1, peakPerHr);
  const W = 600;
  const HSVG = 100;
  const polyPoints = seriesPoints
    .map((p, i) => {
      const x = (i / Math.max(1, seriesPoints.length - 1)) * W;
      const y = HSVG - (p.tweet_count / max) * 90 - 5;
      return `${x},${y}`;
    })
    .join(" ");
  const fillPoints = `0,${HSVG} ${polyPoints} ${W},${HSVG}`;

  const stroke =
    agg.velocity24 === null
      ? COLORS.textDim
      : agg.velocity24 >= 0
        ? COLORS.up
        : COLORS.down;

  const volForTf =
    tf === "24h" ? agg.vol24 : tf === "7d" ? agg.vol7d : agg.vol30d;

  return (
    <div style={tStyles.drillBackdrop} onClick={onClose}>
      <div style={tStyles.drill} onClick={(e) => e.stopPropagation()}>
        <div style={tStyles.drillHeader}>
          <span style={tStyles.drillTicker}>
            ${topic.name.toUpperCase().replace(/\s+/g, "_")}
          </span>
          <span style={tStyles.drillName}>{topic.name}</span>
          <span style={tStyles.drillCat}>· company</span>
          <button style={tStyles.drillWatch} onClick={onToggleWatch}>
            {watched ? "★ WATCHED" : "☆ WATCH"}
          </button>
          <button style={tStyles.drillClose} onClick={onClose}>
            ✕
          </button>
        </div>
        <div style={tStyles.drillStats}>
          <div style={tStyles.drillStat}>
            <span style={tStyles.drillStatLabel}>MENTIONS · {tf.toUpperCase()}</span>
            <span style={tStyles.drillStatValue}>{fmtRaw(volForTf)}</span>
          </div>
          <div style={tStyles.drillStat}>
            <span style={tStyles.drillStatLabel}>VELOCITY 24H</span>
            <span
              style={{
                ...tStyles.drillStatValue,
                color: stroke,
              }}
            >
              {agg.velocity24 === null
                ? "—"
                : `${agg.velocity24 >= 0 ? "+" : ""}${agg.velocity24.toFixed(1)}%`}
            </span>
          </div>
          <div style={tStyles.drillStat}>
            <span style={tStyles.drillStatLabel}>VOL · 7D</span>
            <span style={tStyles.drillStatValue}>{fmtRaw(agg.vol7d)}</span>
          </div>
          <div style={tStyles.drillStat}>
            <span style={tStyles.drillStatLabel}>COVERAGE 24H</span>
            <span style={tStyles.drillStatValue}>
              {Math.round((agg.coverage24 / 24) * 100)}%
            </span>
          </div>
        </div>
        <div style={tStyles.drillSparkBox}>
          <div style={tStyles.drillSparkHead}>
            <span>MENTION VOLUME · last 7d hourly</span>
            <span>peak {fmtRaw(peakPerHr)} / hr</span>
          </div>
          {hires === null ? (
            <div style={{ height: 100, color: COLORS.textDim, fontSize: 11, paddingTop: 40, textAlign: "center" }}>
              loading…
            </div>
          ) : seriesPoints.length === 0 ? (
            <div style={{ height: 100, color: COLORS.textDim, fontSize: 11, paddingTop: 40, textAlign: "center" }}>
              no buckets cached for this range yet — run a sync to populate
            </div>
          ) : (
            <svg viewBox={`0 0 ${W} ${HSVG}`} style={{ width: "100%", height: HSVG }}>
              <defs>
                <linearGradient id="drillG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={stroke} stopOpacity="0" />
                </linearGradient>
              </defs>
              <polyline points={fillPoints} fill="url(#drillG)" stroke="none" />
              <polyline
                points={polyPoints}
                fill="none"
                stroke={stroke}
                strokeWidth="1.5"
              />
              {[0.25, 0.5, 0.75].map((g) => (
                <line
                  key={g}
                  x1="0"
                  x2={W}
                  y1={HSVG - g * 90 - 5}
                  y2={HSVG - g * 90 - 5}
                  stroke={COLORS.border}
                  strokeDasharray="2 4"
                />
              ))}
            </svg>
          )}
        </div>
        <div style={tStyles.drillBody}>
          <div style={{ color: COLORS.textDim, fontSize: 11, lineHeight: 1.6 }}>
            <div style={{ color: COLORS.textDimmer, fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>
              QUERY · spec #{topic.spec.id}
            </div>
            <div style={{ fontFamily: "inherit", color: COLORS.text }}>
              {topic.spec.full_query}
            </div>
            <div style={{ color: COLORS.textDimmer, fontSize: 10, letterSpacing: 1, marginTop: 12 }}>
              hash · {topic.spec.query_hash.slice(0, 16)}…
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <a
              href={`https://x.com/search?q=${encodeURIComponent(
                `${topic.spec.full_query} (min_faves:15 OR min_replies:3 OR min_retweets:5)`,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              title="opens x.com search (default Top sort) with the spec's full query plus an engagement floor — operators we can't use via the API but consumer x.com search supports"
              style={{
                background: "transparent",
                border: `1px solid ${COLORS.accent}`,
                color: COLORS.accent,
                padding: "8px 16px",
                fontFamily: "inherit",
                fontSize: 11,
                cursor: "pointer",
                letterSpacing: 1,
                textDecoration: "none",
              }}
            >
              VIEW ON X ↗
            </a>
            <BackfillButton
              queryHash={topic.spec.query_hash}
              label="Backfill 30 days"
              days={30}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function BackfillButton({
  queryHash,
  label,
  days,
}: {
  queryHash: string;
  label: string;
  days: number;
}) {
  const hasAdmin = useHasAdminKey();
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "previewing" }
    | {
        kind: "preview";
        mills: number;
        calls: number;
        gaps: number;
        endpoint: "all" | "recent";
      }
    | { kind: "running" }
    | { kind: "done"; written: number; mills: number }
    | { kind: "error"; msg: string }
  >({ kind: "idle" });

  const onClick = async () => {
    const end = nowAlignedToHourSec();
    const start = end - days * 24 * 3600;
    if (state.kind === "idle" || state.kind === "done" || state.kind === "error") {
      setState({ kind: "previewing" });
      try {
        const cost = await api.gapCost(queryHash, start, end + 3600);
        if (cost.gaps === 0) {
          setState({ kind: "done", written: 0, mills: 0 });
          return;
        }
        setState({
          kind: "preview",
          mills: cost.cost_mills,
          calls: cost.api_calls,
          gaps: cost.gaps,
          endpoint: cost.endpoint,
        });
      } catch (e) {
        setState({
          kind: "error",
          msg: e instanceof Error ? e.message : String(e),
        });
      }
      return;
    }
    if (state.kind === "preview") {
      setState({ kind: "running" });
      try {
        const r = await api.sync(queryHash, start, end + 3600);
        if (r.errors.length > 0) {
          setState({ kind: "error", msg: r.errors.join("; ") });
        } else {
          setState({ kind: "done", written: r.buckets_written, mills: r.cost_mills });
        }
      } catch (e) {
        setState({
          kind: "error",
          msg: e instanceof Error ? e.message : String(e),
        });
      }
    }
  };

  if (!hasAdmin) return null;

  let txt: string;
  if (state.kind === "idle") txt = label;
  else if (state.kind === "previewing") txt = "checking gaps…";
  else if (state.kind === "preview")
    txt = `Confirm: ${state.gaps} buckets · ~${state.calls} ${state.endpoint} calls · ${fmtMillsUsd(state.mills)}`;
  else if (state.kind === "running") txt = "fetching…";
  else if (state.kind === "done")
    txt = `✓ wrote ${state.written} buckets · ${fmtMillsUsd(state.mills)} spent`;
  else txt = `✗ ${state.msg}`;

  return (
    <button style={{ ...tStyles.drillButton, marginTop: 0 }} onClick={onClick}>
      {txt}
    </button>
  );
}
